import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, Users, FileDown } from "lucide-react";
import api from "@/lib/api";
import Member360 from "@/components/crm/Member360";
import { toast } from "sonner";

export default function AdminClientsPanel() {
  const [q, setQ] = useState("");
  const [centerId, setCenterId] = useState("");
  const [enhancerId, setEnhancerId] = useState("");
  const [pkg, setPkg] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState(null);

  const params = { q, center_id: centerId, enhancer_id: enhancerId, package: pkg, date_from: from, date_to: to };
  const { data, isLoading } = useQuery({
    queryKey: ["admin-clients", params],
    queryFn: () => api.get("/admin/clients", { params }).then((r) => r.data),
  });
  const { data: centers = [] } = useQuery({ queryKey: ["locations-pub"], queryFn: () => api.get("/locations").then((r) => r.data) });
  const { data: enhancers = [] } = useQuery({ queryKey: ["admin-enh-options"], queryFn: () => api.get("/admin/enhancers-options").then((r) => r.data) });
  const clients = data?.clients || [];

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/clients.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "clients.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Client list CSV downloaded.");
    } catch { toast.error("Couldn't export the CSV."); }
  };

  if (selected) return <Member360 userId={selected} onBack={() => setSelected(null)} />;

  return (
    <div data-testid="admin-clients-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B]">Clients & purchases</h2>
          <p className="text-sm text-[#4A5D6B]">Every client across all centers and enhancers — open one for full purchase, booking, and package history.</p>
        </div>
        <button onClick={downloadCsv} data-testid="admin-clients-csv" className="eyw-btn-ghost !py-2 text-sm"><FileDown size={15} /> Export CSV</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2 mb-5" data-testid="admin-clients-filters">
        <div className="relative lg:col-span-2">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email…" data-testid="admin-clients-search" className="eyw-input !pl-9 w-full" />
        </div>
        <select value={centerId} onChange={(e) => setCenterId(e.target.value)} data-testid="admin-clients-center" className="eyw-input">
          <option value="">All centers</option>
          {centers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={enhancerId} onChange={(e) => setEnhancerId(e.target.value)} data-testid="admin-clients-enhancer" className="eyw-input">
          <option value="">All enhancers</option>
          {enhancers.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <input value={pkg} onChange={(e) => setPkg(e.target.value)} placeholder="Package type…" data-testid="admin-clients-package" className="eyw-input" />
        <div className="flex gap-1.5">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="admin-clients-from" className="eyw-input flex-1 min-w-0" />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="admin-clients-to" className="eyw-input flex-1 min-w-0" />
        </div>
      </div>
      {isLoading ? <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p> : (
        <div className="eyw-card overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#F4F1EB] text-[#4A5D6B]">
              <tr><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Active packages</th><th className="text-left px-4 py-3">Balances</th><th /></tr>
            </thead>
            <tbody className="divide-y divide-[#E5E0D8]">
              {clients.map((c) => (
                <tr key={c.client_id} className="hover:bg-[#F9F7F3]" data-testid={`admin-client-row-${c.client_id}`}>
                  <td className="px-4 py-3 text-[#1A242B]">{c.name || "—"}</td>
                  <td className="px-4 py-3 text-[#4A5D6B]">{c.email || "—"}</td>
                  <td className="px-4 py-3 text-[#144F75] font-medium">{c.packages_active}</td>
                  <td className="px-4 py-3 text-[#8FA89B] text-xs max-w-[240px] truncate">{c.package_summary || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {c.user_id ? (
                      <button onClick={() => setSelected(c.user_id)} data-testid={`admin-client-open-${c.client_id}`} className="text-xs text-[#144F75] font-medium hover:underline">Full profile</button>
                    ) : <span className="text-[11px] text-[#8FA89B]">no account</span>}
                  </td>
                </tr>
              ))}
              {clients.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#8FA89B]"><Users size={22} className="mx-auto mb-2 opacity-60" /> No clients match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
